import * as semver from 'semver';

import {
    HttpClient
} from '@angular/common/http';

import {
    firstValueFrom
} from 'rxjs';

import {
    inject,
    Injectable
} from '@angular/core';

import {
    EnvironmentInfoService,
    SystemUtilsService
} from 'src/app/core';

import {
    upperFirst
} from 'src/app/shared/string-utils';

const { tilbyVersion } = require('app/tilby.properties.json');
const updateCheckUrl = "https://checkupdates.scloby.com/latest.json";

type updateInfo = {
    latest_version: string,
    immediate: boolean,
    url?: string
}

type apkInfo = {
    name: string,
    path: string,
    size: number,
    app: {
        name: string
        package: string,
        version: {
            code: number,
            name: string
        }
    }
}

type updateCheckResult = {
    updateAvailable: boolean,
    updateRequired: boolean,
    hasUpdateUrl: boolean,
    platformClass: string
}

@Injectable({
    providedIn: 'root'
})
export class UpdateManagerService {
    private readonly environmentInfoService = inject(EnvironmentInfoService);
    private readonly httpClient = inject(HttpClient);
    private readonly systemUtilsService = inject(SystemUtilsService);

    private latestUpdateInfo?: updateInfo;
    private latestUpdateStatus?: updateCheckResult
    private platformClass?: string;

    private async hasGooglePlayStore(): Promise<boolean> {
        return new Promise((resolve, reject) => {
            if(window.appAvailability) {
                window.appAvailability.check('com.android.vending', (info: any) => resolve(true), () => resolve(false));
            } else {
                resolve(false);
            }
        })
    }

    private async getPlatformClass(): Promise<string> {
        if(!this.platformClass) {
            if(this.environmentInfoService.isElectronApp()) {
                switch(this.environmentInfoService.getUserAgentOS()) {
                    case 'Windows':
                        this.platformClass = 'windows';
                    break;
                    case 'macOS':
                        this.platformClass = 'macos';
                    break;
                    default:
                        this.platformClass = 'unknown';
                    break;
                }
            } else if(this.environmentInfoService.isMobileApp()) {
                if(this.environmentInfoService.isAppleMobile()) {
                    this.platformClass = 'ios';
                } else {
                    let hasPlayServices = await this.hasGooglePlayStore();

                    if(hasPlayServices) {
                        this.platformClass = 'android-playservices';
                    } else {
                        const manufacturer = window.device.manufacturer || '';

                        switch(upperFirst(manufacturer.toLowerCase())) {
                            case 'Huawei':
                                this.platformClass = 'android-huawei';
                            break;
                            default:
                                this.platformClass = 'android-apk';
                            break;
                        }
                    }
                }
            } else {
                this.platformClass = 'web';
            }
        }

        return this.platformClass;
    }

    public async checkForUpdates(): Promise<updateCheckResult> {
        let checkResult: updateCheckResult = {
            updateAvailable: false,
            updateRequired: false,
            hasUpdateUrl: false,
            platformClass: ''
        }

        checkResult.platformClass = await this.getPlatformClass();

        if (!['web'].includes(checkResult.platformClass)) {
            try {
                // Download update info
                const updateInfoObj = await firstValueFrom(this.httpClient.get<any>(updateCheckUrl));

                if(updateInfoObj) {
                    this.latestUpdateInfo = updateInfoObj[checkResult.platformClass];
                }

                //If there is a new version
                if (semver.gt(this.latestUpdateInfo?.latest_version||'', tilbyVersion)) {
                    checkResult.updateAvailable = true;
                    checkResult.hasUpdateUrl = !!this.latestUpdateInfo?.url;

                    if (this.latestUpdateInfo?.immediate) { //Mandatory update
                        checkResult.updateRequired = true;
                    }
                }
            } catch (err) {
                //Nothing to do
            }
        }

        this.latestUpdateStatus = checkResult;

        return Object.assign({}, this.latestUpdateStatus);
    }

    public async updateNow(onProgressChange: any) {
        let updateUrl = this.latestUpdateInfo?.url;

        if (!updateUrl) {
            return;
        }

        //If the apk updater plugin is found in the current environment, download the apk directly
        if (updateUrl.endsWith('.apk') && window.ApkUpdater) {
            try {
                //Start the apk download
                let apkInfo: apkInfo = await window.ApkUpdater.download(updateUrl, {
                    onDownloadProgress: (status: any) => {
                        if (typeof onProgressChange === 'function') {
                            onProgressChange(status.progress);
                        }
                    }
                });

                //Check if the bundle name and the version are the ones we expect
                if (apkInfo?.app.package === 'com.scloby.pos') {
                    //Launch the package installation
                    await window.ApkUpdater.install();
                }
            } catch (error) {
                //TODO: handle download errors
            }
        } else {
            //Open the update url for all of the other cases
            this.systemUtilsService.openExternalLink(updateUrl);
        }
    }

    public getLatestUpdateInfo(): updateInfo | undefined {
        return structuredClone(this.latestUpdateInfo);
    }

    public getLatestUpdateStatus(): updateCheckResult | undefined {
        return structuredClone(this.latestUpdateStatus)
    }
}
